/**
 * The account-level endpoints the shell needs before a workspace store exists (architecture §5.1, §5.6, §6.2):
 * `GET /api/session`, dev login and log out. Workspace data never goes through here — that is the sync client.
 */
import { appChannel } from '../broadcast/channel';

export interface SessionWorkspace {
  readonly id: string;
  readonly urlKey: string;
  readonly name: string;
  readonly role: string;
}

export interface SessionInfo {
  readonly account: { readonly id: string; readonly email: string | null } | null;
  readonly workspaces: readonly SessionWorkspace[];
  /** whether this account may create a workspace now (ADR-0073: a WORKSPACE_CREATORS address, or P0's first one) */
  readonly canCreateWorkspace: boolean;
  readonly devLogin: boolean;
  readonly build: string;
  readonly modelVersion: number;
}

/** Monotonic client build number (`X-Quadratic-Client: web/<build>`), injected at build time. */
export const CLIENT_BUILD: number = Number.parseInt(String(import.meta.env.VITE_QD_BUILD ?? '1'), 10) || 1;

// header names are case-insensitive; lower case keeps the product name out of UI code (lint)
const CLIENT_HEADER = { 'x-quadratic-client': `web/${CLIENT_BUILD}` };

let cached: Promise<SessionInfo> | null = null;

class SessionError extends Error {
  override name = 'SessionError';
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function load(): Promise<SessionInfo> {
  const res = await fetch('/api/session', { credentials: 'same-origin', headers: { Accept: 'application/json', ...CLIENT_HEADER } });
  if (!res.ok) throw new SessionError(`session ${res.status}`, res.status);
  const body = (await res.json()) as Partial<SessionInfo>;
  return {
    account: body.account ?? null,
    workspaces: body.workspaces ?? [],
    canCreateWorkspace: body.canCreateWorkspace === true,
    devLogin: body.devLogin === true,
    build: typeof body.build === 'string' ? body.build : 'dev',
    modelVersion: typeof body.modelVersion === 'number' ? body.modelVersion : 0,
  };
}

/** The current session (one request per page load; `refresh` after sign-in or log out). */
export function getSession(o: { refresh?: boolean } = {}): Promise<SessionInfo> {
  if (!cached || o.refresh) {
    cached = load();
    cached.catch(() => {
      cached = null;
    });
  }
  return cached;
}

/** Dev login (DEV_LOGIN=1 only): starts a session for a seeded or allowlisted email. */
export async function devLogin(email: string): Promise<void> {
  const res = await fetch('/api/auth/dev-login', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...CLIENT_HEADER },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const b = (await res.json().catch(() => ({}))) as { message?: string };
    throw new SessionError(b.message ?? 'This account can’t sign in.', res.status);
  }
  await getSession({ refresh: true });
}

/**
 * Log out: the server revokes every session of the account (ADR-0026); other tabs hear `logout` and wipe their
 * stores. The page then reloads into /login, which drops every in-memory store.
 */
export async function logOut(): Promise<void> {
  const s = await getSession().catch(() => null);
  try {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin', headers: CLIENT_HEADER });
  } finally {
    if (s?.account) appChannel.post({ type: 'logout', accountId: s.account.id });
    globalThis.location.assign('/login');
  }
}
